import { useState } from 'react'
import {
  AlertIcon,
  Avatar,
  CheckIcon,
  DataSurface,
  InfoIcon,
  VoiceIcon,
  Pagination,
  usePagination,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getSchoolAnnouncements,
  postAnnouncement,
  type AnnouncementRow,
} from '../../../shared/api/announcements'
import type { StaffAuth } from '../../../shared/api/teaching'
import { TEACHER_COPY, describeError, type Lang } from './copy'

export interface ClassNoticesProps {
  classId: string
  lang?: Lang
  auth?: StaffAuth
  teacherName?: string
}

/*
 * 🔴 `teacherName` HAS NO DEFAULT, AND IT HAD ONE HERE TOO.
 *
 * Both this component and `PaginatedNoticesList` below declared
 * `teacherName = 'Nishant'` — a real person's first name, hardcoded as a
 * parameter default in two places. Any school whose notices came back without
 * an `authorLabel` saw a stranger credited as the author of its own
 * announcements. `TeacherApp` does pass the signed-in teacher's name, so the
 * default was only ever reachable by accident; that is precisely what made it
 * dangerous, because nothing in normal use would have surfaced it.
 */
export function ClassNotices({ classId, lang = 'en', auth, teacherName }: ClassNoticesProps) {
  const t = TEACHER_COPY[lang]
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [posted, setPosted] = useState<AnnouncementRow | null>(null)
  const [version, setVersion] = useState(0)

  const board = useResource<AnnouncementRow[]>(
    (signal) => getSchoolAnnouncements({ ...auth, signal }, classId),
    ['class-notices', classId, String(version)],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const valid = title.trim().length >= 3 && body.trim().length >= 3

  const handlePost = () => {
    setBusy(true)
    setFailed(false)
    setPosted(null)
    void postAnnouncement(
      { title: title.trim(), body: body.trim(), whatsapp: false, classId },
      auth,
    )
      .then((row) => {
        setPosted(row)
        setTitle('')
        setBody('')
        setVersion((n) => n + 1)
      })
      .catch(() => {
        setFailed(true)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      {/* ── Left Column: Create a Notice (7 cols) ── */}
      <div className="lg:col-span-7 rounded-3xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
        <div>
          {/* Header */}
          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-primary border border-pink-100 shadow-2xs">
              <VoiceIcon size={20} strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="font-dash text-dash-md font-extrabold text-ink">
                Create a Notice
              </h2>
              <p className="mt-1 font-dash text-dash-xs font-medium text-muted leading-relaxed max-w-[65ch]">
                A notice every family at this school can read on their phone.
                <br />
                It appears on the parent app straight away.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {/* Field: Headline */}
            <div>
              <label htmlFor="notice-title" className="mb-1.5 block font-dash text-dash-xs font-bold text-ink">
                Headline
              </label>
              <div className="flex items-center gap-3 rounded-2xl border border-pink-200/90 bg-surface px-3 py-2.5 shadow-2xs transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-pink-100">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-primary border border-pink-100 font-extrabold text-xs">
                  T
                </div>
                <input
                  id="notice-title"
                  type="text"
                  value={title}
                  maxLength={80}
                  placeholder="Sports day is on Friday"
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-transparent font-dash text-dash-sm font-bold text-ink outline-none placeholder:text-muted/60"
                />
                <span className="tabular font-dash text-dash-xs font-bold text-muted/70 shrink-0">
                  {title.length}/80
                </span>
              </div>
            </div>

            {/* Field: What you want to say */}
            <div>
              <label htmlFor="notice-body" className="mb-1.5 block font-dash text-dash-xs font-bold text-ink">
                What you want to say
              </label>
              <div className="flex flex-col rounded-2xl border border-pink-200/90 bg-surface p-3.5 shadow-2xs transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-pink-100 min-h-[140px]">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-primary border border-pink-100 mb-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"></path>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                  </svg>
                </div>
                <textarea
                  id="notice-body"
                  value={body}
                  rows={4}
                  maxLength={1200}
                  placeholder="Sports day is on Friday the 22nd. Please send your child in sports clothes with a water bottle."
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full flex-1 bg-transparent font-dash text-dash-sm font-medium text-ink outline-none resize-none placeholder:text-muted/60"
                />
                <div className="flex justify-end pt-1">
                  <span className="tabular font-dash text-dash-xs font-bold text-muted/70">
                    {body.length}/1200
                  </span>
                </div>
              </div>
            </div>

            {/*
              Warning note.

              ⚠️ THE PILL TOKENS, NOT SIX AMBER TAILWIND CLASSES. The
              `pill-warning-*` family is the token set for exactly this note,
              and `InfoIcon` replaces a bare "ℹ" character — which is not an
              emoji but a letterlike symbol, so it renders as a serif glyph in
              some font stacks, a sans one in others, and is missing entirely
              from a few.
            */}
            <div className="flex items-start gap-3 rounded-2xl border border-pill-warning-line bg-pill-warning-bg p-4 shadow-2xs">
              <span className="mt-0.5 shrink-0 text-pill-warning-fg">
                <InfoIcon size={16} />
              </span>
              <p className="font-dash text-dash-xs font-extrabold text-pill-warning-fg leading-snug">
                A notice cannot be edited or taken back once it is posted — the messages have already gone. To correct one, post another.
              </p>
            </div>
          </div>
        </div>

        {/* Action Button & Status */}
        <div className="mt-6 flex flex-col gap-3">
          <div>
            <button
              type="button"
              disabled={busy || !valid}
              onClick={handlePost}
              className="rounded-full bg-primary hover:bg-primary-deep active:scale-98 text-white px-6 py-3 font-dash text-dash-sm font-extrabold shadow-md transition-all flex items-center gap-2.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              <span>{t.noticePost || "Post this notice"}</span>
            </button>
          </div>

          {posted && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 font-dash text-dash-xs font-bold text-green-800 flex items-center gap-2">
              <CheckIcon size={16} />
              <span>{posted.whatsapp ? t.noticeQueued(posted.enqueued) : t.noticePosted}</span>
            </div>
          )}
          {failed && (
            <div className="rounded-xl border border-pink-200 bg-pink-50 px-4 py-2.5 font-dash text-dash-xs font-bold text-pink-800 flex items-center gap-2">
              <AlertIcon size={16} />
              <span>{t.noticeFailed}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Right Column: Recent notices (5 cols) ── */}
      <div className="lg:col-span-5 rounded-3xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-pink-50 text-primary border border-pink-100">
              <VoiceIcon size={18} strokeWidth={2.2} />
            </div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">
              Recent notices
            </h2>
          </div>
          <button type="button" className="font-dash text-dash-xs font-extrabold text-primary hover:underline cursor-pointer">
            View all
          </button>
        </div>

        <DataSurface
          state={board.state}
          skeletonRows={3}
          emptyMessage={t.noticeNone}
          errorMessage={describeError(board.error, lang)}
          onRetry={board.retry}
        >
          {board.data === null ? null : (
            <PaginatedNoticesList
              rows={board.data}
              teacherName={teacherName}
              lang={lang}
            />
          )}
        </DataSurface>
      </div>
    </div>
  )
}

/*
 * ✅ `NOTICE_EMOJIS` IS GONE, AND IT WAS DECORATION THAT COULD LIE.
 *
 * Nine emoji were cycled by LIST INDEX — `NOTICE_EMOJIS[idx % 9]` — so the
 * picture beside a notice had nothing to do with what the notice said. A
 * message about a child's absence could be handed a football; a sports day
 * could be handed a paint palette. Worse, the same notice CHANGED PICTURE the
 * moment another was posted above it, because every row's index shifted by
 * one. §12's rule is that nothing on a data screen carries meaning it does not
 * have, and a symbol that reshuffles itself carries anti-meaning.
 *
 * The disc now holds the sender's initials, which IS a fact about the row.
 */

function PaginatedNoticesList({
  rows,
  /*
   * 🔴 NO DEFAULT NAME. This read `teacherName = 'Nishant'` — a real person's
   * first name, hardcoded as the parameter default, and therefore printed as
   * the author of every notice whose `authorLabel` the server left empty. A
   * school using this product would have seen a stranger credited with its own
   * announcements. A fallback that invents an author is worse than a blank one.
   */
  teacherName,
  lang,
}: {
  rows: AnnouncementRow[]
  teacherName?: string
  lang: Lang
}) {
  const t = TEACHER_COPY[lang]
  const { paginatedItems, page, totalPages, setPage, totalItems } = usePagination(rows, 7)

  return (
    <>
      <div className="flex flex-col gap-3.5">
        {paginatedItems.map((row) => {
          const formattedDate = new Date(row.createdAt).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          })
          /* The server's label first, then whoever is signed in, then the
             copy's neutral word. Never an invented name. */
          const author = row.authorLabel || teacherName || t.messageYou

          return (
            <div
              key={row.id}
              className="rounded-2xl border border-line bg-surface p-4 shadow-sm transition-all hover:shadow-card flex flex-col gap-1.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={author} size={40} />
                  <h3 className="truncate font-dash text-dash-sm font-extrabold text-ink">
                    {row.title}
                  </h3>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-dash-xs text-muted font-dash font-medium">
                  <span className="tabular">{formattedDate}</span>
                  <button type="button" className="text-muted hover:text-ink p-1 rounded-md transition-colors cursor-pointer">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>
                  </button>
                </div>
              </div>

              <p className="font-dash text-dash-xs text-ink-soft whitespace-pre-line pl-[52px] -mt-2">
                {row.body}
              </p>

              <div className="pl-[52px] flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200/90 bg-[#fffbeb] px-2.5 py-0.5 font-dash text-dash-xs font-bold text-amber-900 shadow-2xs">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                    <line x1="12" y1="18" x2="12.01" y2="18"></line>
                  </svg>
                  <span>In the app</span>
                </span>
                <span className="font-dash text-dash-xs font-semibold text-muted">
                  • {author}
                </span>
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={totalItems}
        />
      </div>
    </>
  )
}
